// D:\directiveai-react\agthealth\agthealth-frontend\src\components\Login\views\RegisterView.js

import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Step,
  StepLabel,
  Autocomplete, 
  FormHelperText, 
  InputAdornment,
  Alert,
  Snackbar
} from '@mui/material';
import { motion } from 'framer-motion';
import { ChevronRight, ChevronLeft, Search, Stethoscope, CheckCircle } from 'lucide-react';
import { 
  RegisterCard, 
  ActionButton, 
  BackButton, 
  StyledTextField, 
  AuthCard 
} from '../styles/StyledComponents';
import { validateRUT, validateSSN } from '../utils/validators';
import { HEALTH_GREEN } from '../utils/mockData';
import AppleSpinner from '../../../common/AppleSpinner';
import CountrySelector from '../components/CountrySelector';
import StepperWrapper from '../components/StepperWrapper';
import CredentialCapture from '../components/CredentialCapture';
import PasswordFields from '../components/PasswordFields';
import UserTypeSelector from '../components/UserTypeSelector';
import useCamera from '../hooks/useCamera';
import agthealthService from '../../../services/agthealthService';

const RegisterView = ({ onBackToLogin, isMobile, userType, onUserTypeChange }) => {
  // Registration states
  const [activeStep, setActiveStep] = useState(0);
  const [country, setCountry] = useState('');
  const [identificationNumber, setIdentificationNumber] = useState('');
  const [identificationError, setIdentificationError] = useState('');
  const [healthCenter, setHealthCenter] = useState(null);
  const [healthCenterError, setHealthCenterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCenters, setFilteredCenters] = useState([]);
  const [healthCenters, setHealthCenters] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'error' });
  
  // Use camera hook
  const cameraHook = useCamera();

  // Get registration steps
  const getRegistrationSteps = () => {
    return isMobile
      ? ['Country', 'ID', 'Center', 'Info', 'Password']
      : ['Country', 'Identification', 'Health Center', 'Personal Info', 'Create Password'];
  };

  const registrationSteps = getRegistrationSteps();

  // Load health centers when country changes
  useEffect(() => {
    if (country) {
      loadHealthCenters(country);
    }
  }, [country]);

  // Load health centers from API
  const loadHealthCenters = async (countryCode) => {
    try {
      const response = await agthealthService.getHealthCentersByCountry(countryCode);
      if (response.success && response.data) {
        setHealthCenters(response.data);
      }
    } catch (error) {
      console.error('Error loading health centers:', error);
      setAlert({
        open: true,
        message: 'Error al cargar centros de salud',
        severity: 'error'
      });
    }
  };

  // Filter health centers when search term changes
  useEffect(() => {
    if (country && searchTerm) {
      const centers = healthCenters || [];
      const filtered = centers.filter(center => 
        center.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCenters(filtered);
    }
  }, [country, searchTerm, healthCenters]);

  // Validate email format
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Handle password changes
  const handleNewPasswordChange = (e) => {
    setPassword(e.target.value);
    validatePasswords(e.target.value, confirmPassword);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    validatePasswords(password, e.target.value);
  };

  const validatePasswords = (newPwd, confirmPwd) => {
    if (confirmPwd && newPwd !== confirmPwd) {
      setPasswordError('Passwords do not match');
      return false;
    } else if (newPwd && newPwd.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return false;
    } else {
      setPasswordError('');
      return true;
    }
  };

  // Registration navigation
  const handleNext = () => {
    // Validate before proceeding
    if (activeStep === 1) {
      if (country === 'cl' && !validateRUT(identificationNumber)) {
        setIdentificationError('Invalid RUT format. Example: 12345678-9');
        return;
      } else if (country === 'us' && !validateSSN(identificationNumber)) {
        setIdentificationError('Invalid SSN format. Example: 123-45-6789');
        return;
      }
      setIdentificationError('');
    }
    
    // For step 2 (Health Center), validate health center selection
    if (activeStep === 2) {
      if (!healthCenter) {
        setHealthCenterError('Please select a health center');
        return;
      }
      setHealthCenterError('');
    }
    
    // For step 3 (Personal Info), validate email
    if (activeStep === 3) {
      if (!firstName || !lastName || !email) {
        setAlert({
          open: true,
          message: 'All fields are required',
          severity: 'error'
        });
        return;
      }
      
      if (!validateEmail(email)) {
        setEmailError('Please enter a valid email address');
        return;
      }
      
      setEmailError('');
    }
    
    // For final step (Create Password), validate passwords
    if (activeStep === 4) {
      if (!validatePasswords(password, confirmPassword)) {
        return;
      }
      handleRegister();
      return;
    }
    
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      onBackToLogin();
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleRegister = async () => {
    setIsSubmitting(true);
    
    try {
      const userData = {
        email,
        password,
        countryCode: country,
        identificationNumber,
        healthCenterId: healthCenter.centerId,
        healthCenterName: healthCenter.name,
        firstName,
        lastName
      };
      
      const response = await agthealthService.register(userData);
      
      if (response.success) {
        setRegisterSuccess(true);
      } else {
        throw new Error(response.message || 'Error registering user');
      }
    } catch (error) {
      console.error('Error registering user:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Error registering account',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine if the current step is complete
  const isStepComplete = () => {
    switch (activeStep) {
      case 0: return !!country;
      case 1: return !!identificationNumber && !identificationError;
      case 2: return !!healthCenter;
      case 3: return !!firstName && !!lastName && !!email && !emailError;
      case 4: return password && confirmPassword && !passwordError && password.length >= 8;
      default: return false;
    }
  };

  // Registration step content
  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <CountrySelector 
            selectedCountry={country} 
            onSelectCountry={setCountry} 
          />
        );
      
      case 1:
        return (
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {country === 'cl' 
                ? 'Please enter your RUT (without dots and with dash)' 
                : 'Please enter your Social Security Number'}
            </Typography>
            <StyledTextField
              fullWidth
              label={country === 'cl' ? 'RUT' : 'SSN'}
              variant="outlined"
              value={identificationNumber}
              onChange={(e) => {
                setIdentificationNumber(e.target.value);
                setIdentificationError(''); // Clear error on change
              }}
              placeholder={country === 'cl' ? '12345678-9' : '123-45-6789'}
              error={!!identificationError}
              helperText={identificationError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{ width: 24, height: 16, marginRight: 0 }}>
                      <Box 
                        component="img" 
                        src={`https://flagcdn.com/w40/${country}.png`}
                        alt={`${country === 'cl' ? 'Chile' : 'US'} flag`}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {country === 'cl' 
                ? 'Example: 12345678-9' 
                : 'Example: 123-45-6789'}
            </Typography>
          </Box>
        );
      
      case 2:
        return (
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Select your health center
            </Typography>
            <Autocomplete
              fullWidth
              options={healthCenters || []}
              getOptionLabel={(option) => option.name || ''}
              value={healthCenter}
              onChange={(event, newValue) => {
                setHealthCenter(newValue);
                setHealthCenterError('');
              }}
              onInputChange={(event, newInputValue) => {
                setSearchTerm(newInputValue);
              }}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  label="Search for your health center"
                  variant="outlined"
                  error={!!healthCenterError}
                  helperText={healthCenterError}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={20} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            {healthCenter && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={500}>
                  {healthCenter.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {healthCenter.address || 'No address available'}
                </Typography>
              </Box>
            )}
          </Box>
        );
      
      case 3:
        return (
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Enter your personal information
            </Typography>
            
            <StyledTextField
              fullWidth
              label="First Name"
              variant="outlined"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              margin="normal"
            />
            
            <StyledTextField
              fullWidth
              label="Last Name"
              variant="outlined"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              margin="normal"
            />
            
            <StyledTextField
              fullWidth
              label="Email"
              variant="outlined"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError('');
              }}
              error={!!emailError}
              helperText={emailError}
              margin="normal"
            />
          </Box>
        );
      
      case 4:
        return (
          <PasswordFields
            newPassword={password}
            confirmPassword={confirmPassword}
            passwordError={passwordError}
            onNewPasswordChange={handleNewPasswordChange}
            onConfirmPasswordChange={handleConfirmPasswordChange}
            isMobile={isMobile}
          />
        );
      
      default:
        return 'Unknown step';
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  if (registerSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: '100%' }}
      >
        <AuthCard>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            textAlign: 'center',
            my: 4
          }}>
            <CheckCircle size={isMobile ? 64 : 80} color={HEALTH_GREEN} />
            <Typography variant={isMobile ? "h5" : "h4"} sx={{ mt: 3, fontWeight: 600 }}>
              Registration Complete
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Your account has been created successfully.
              You can now access all features of AGT Health.
            </Typography>
            
            <ActionButton 
              variant="contained"
              disableElevation
              fullWidth
              onClick={onBackToLogin}
              sx={{ mt: 4 }}
            >
              Continue to Dashboard
            </ActionButton>
          </Box>
        </AuthCard>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ width: '100%' }}
    >
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
      
      <RegisterCard>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          mb: isMobile ? 2 : 3, 
          alignItems: 'center'
        }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            sx={{ 
              fontWeight: 600, 
              mb: 1, 
              display: 'flex', 
              alignItems: 'center' 
            }}
          >
            AGT Health
            <motion.span
              initial={{ scale: 1 }}
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1, repeat: Infinity, repeatDelay: 5 }}
              style={{ 
                color: HEALTH_GREEN, 
                fontSize: isMobile ? '24px' : '32px', 
                fontWeight: 700, 
                marginLeft: '2px',
                position: 'relative',
                top: '-8px'
              }}
            >
              +
            </motion.span>
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Stethoscope size={isMobile ? 18 : 20} color={HEALTH_GREEN} />
            <Typography variant="subtitle1" color="text.secondary">
              Create Healthcare Professional Account
            </Typography>
          </Box>
        </Box>

        <UserTypeSelector 
          userType={userType} 
          onUserTypeChange={onUserTypeChange} 
        />

        <StepperWrapper 
          steps={registrationSteps}
          activeStep={activeStep}
        />

        {getStepContent()}

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <BackButton
            startIcon={<ChevronLeft size={16} />}
            onClick={handleBack}
            disabled={isSubmitting}
            fullWidth={isMobile}
          >
            {activeStep === 0 ? 'Back to Login' : 'Back'}
          </BackButton>
          
          <ActionButton 
            variant="contained"
            disableElevation
            disabled={!isStepComplete() || isSubmitting}
            onClick={handleNext}
            fullWidth={isMobile}
            endIcon={activeStep === registrationSteps.length - 1 ? null : <ChevronRight size={16} />}
          >
            {isSubmitting ? <AppleSpinner sizeDesktop={24} sizeMobile={20} /> : 
              (activeStep === registrationSteps.length - 1 ? 'Complete Registration' : 'Continue')}
          </ActionButton>
        </Box>
      </RegisterCard>
    </motion.div>
  );
};

export default RegisterView;